import { useEffect, useMemo, useState } from "react"
import { Link } from "../lib/router"
import { Loader2, Search, ChevronLeft, Waves } from "lucide-react"
import { BRAND } from "../lib/brand"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

const STROKE_META = {
  freestyle: { labelKey: "stroke_freestyle", color: "#3B82F6" },
  backstroke: { labelKey: "stroke_backstroke", color: "#A855F7" },
  breaststroke: { labelKey: "stroke_breaststroke", color: "#10B981" },
  butterfly: { labelKey: "stroke_butterfly", color: "#F59E0B" },
  medley: { labelKey: "stroke_medley", color: "#EF4444" },
  relay: { labelKey: "stroke_relay", color: "#06B6D4" },
  other: { labelKey: "stroke_other", color: "#94A3B8" },
}

function detectStroke(eventName) {
  const s = (eventName || "").toLowerCase()
  if (s.includes("medley")) return "medley"
  if (s.includes("relay")) return "relay"
  if (s.includes("freestyle")) return "freestyle"
  if (s.includes("backstroke")) return "backstroke"
  if (s.includes("breaststroke")) return "breaststroke"
  if (s.includes("butterfly")) return "butterfly"
  return "other"
}

function detectAgeCategory(eventName) {
  const match = /(\d+)\s+Year\s+Olds/i.exec(eventName || "")
  return match ? `${match[1]} Year Olds` : null
}

// A clean rank (no tie marker, not DQ/NS/DNF) is podium-eligible.
function isCleanRank(rank) {
  return rank !== "---" && !rank.startsWith("*")
}

export default function LiveResults() {
  const [meetEvents, setMeetEvents] = useState([]) // real site events that have published results
  const [loadingMeetEvents, setLoadingMeetEvents] = useState(true)
  const [selectedMeetEventId, setSelectedMeetEventId] = useState(null)

  const [eventResults, setEventResults] = useState([])
  const [loadingResults, setLoadingResults] = useState(false)
  const [error, setError] = useState("")

  const [genderFilter, setGenderFilter] = useState("")
  const [strokeFilter, setStrokeFilter] = useState("")
  const [ageFilter, setAgeFilter] = useState("")
  const [selectedRaceName, setSelectedRaceName] = useState(null)
  const [search, setSearch] = useState("")
  const { t } = useLanguage()

  useEffect(() => {
    fetch(`${API}/api/results/events`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json() })
      .then((rows) => {
        setMeetEvents(rows)
        setSelectedMeetEventId(rows[0]?.id ?? null)
      })
      .catch(() => setError("Could not load events."))
      .finally(() => setLoadingMeetEvents(false))
  }, [])

  useEffect(() => {
    if (!selectedMeetEventId) return
    setLoadingResults(true)
    setError("")
    setSelectedRaceName(null)
    setGenderFilter("")
    setStrokeFilter("")
    setAgeFilter("")
    setSearch("")
    fetch(`${API}/api/results?event_id=${selectedMeetEventId}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json() })
      .then(setEventResults)
      .catch(() => setError("Could not load results."))
      .finally(() => setLoadingResults(false))
  }, [selectedMeetEventId])

  const races = useMemo(() => {
    const byName = new Map()
    for (const row of eventResults) {
      if (!byName.has(row.event_name)) {
        byName.set(row.event_name, {
          event_name: row.event_name,
          event_number: row.event_number,
          gender: row.gender,
          stroke: detectStroke(row.event_name),
          ageCategory: detectAgeCategory(row.event_name),
        })
      }
    }
    return [...byName.values()].sort((a, b) => a.event_number - b.event_number)
  }, [eventResults])

  const genders = useMemo(() => [...new Set(races.map((e) => e.gender))], [races])
  const strokes = useMemo(() => [...new Set(races.map((e) => e.stroke))], [races])
  const ageCategories = useMemo(() => [...new Set(races.map((e) => e.ageCategory).filter(Boolean))], [races])

  const filteredRaces = races.filter(
    (e) =>
      (!genderFilter || e.gender === genderFilter) &&
      (!strokeFilter || e.stroke === strokeFilter) &&
      (!ageFilter || e.ageCategory === ageFilter),
  )

  useEffect(() => {
    if (filteredRaces.length && !filteredRaces.some((e) => e.event_name === selectedRaceName)) {
      setSelectedRaceName(filteredRaces[0].event_name)
    }
  }, [filteredRaces, selectedRaceName])

  const selectedRace = races.find((e) => e.event_name === selectedRaceName)
  const raceRows = eventResults
    .filter((r) => r.event_name === selectedRaceName)
    .filter((r) => !search || r.athlete.toLowerCase().includes(search.toLowerCase()) || r.team.toLowerCase().includes(search.toLowerCase()))

  const podium = raceRows.filter((r) => isCleanRank(r.rank)).slice(0, 3)
  const podiumIds = new Set(podium.map((r) => r.id))
  const tableRows = raceRows.filter((r) => !podiumIds.has(r.id))
  // Podium display order: 2nd, 1st, 3rd (gold card center, taller).
  const podiumOrdered = [podium[1], podium[0], podium[2]].filter(Boolean)

  return (
    <div className="min-h-screen bg-ink-900">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-ink-900/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-paper-0 transition-colors hover:text-gold-400">
            <ChevronLeft className="h-4 w-4" /> {t("live_results_home")}
          </Link>
          <h1 className="font-display text-lg font-bold text-paper-0">{t("live_results_title")}</h1>
          <div className="w-16" />
        </div>
      </header>

      {/* Event tabs */}
      <div className="mx-auto max-w-6xl px-6 pt-6">
        {loadingMeetEvents ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gold-400" /></div>
        ) : meetEvents.length > 0 ? (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {meetEvents.map((ev) => (
              <button
                key={ev.id}
                onClick={() => setSelectedMeetEventId(ev.id)}
                className={`flex shrink-0 flex-col items-start rounded-lg border px-4 py-2 text-left text-sm font-semibold transition-colors ${
                  selectedMeetEventId === ev.id
                    ? "border-gold-500/50 bg-gold-500/10 text-gold-400"
                    : "border-white/15 text-paper-0 hover:border-white/30"
                }`}
              >
                {ev.title}
                <span className="text-[10px] font-medium uppercase tracking-wider text-mist-500">{ev.date}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <main className="mx-auto max-w-6xl px-6 pb-16 pt-4">
        {error && <div role="alert" className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        {loadingMeetEvents || loadingResults ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
        ) : meetEvents.length === 0 ? (
          <p className="py-24 text-center text-sm text-mist-500">{t("live_results_pending_event")}</p>
        ) : races.length === 0 ? (
          <p className="py-24 text-center text-sm text-mist-500">{t("live_results_pending_race")}</p>
        ) : (
          <>
            {/* Filter pills */}
            <div className="mb-4 flex flex-wrap gap-2">
              {genders.length > 1 && (
                <FilterPill label={t("live_results_all_genders")} value={genderFilter} setValue={setGenderFilter} options={genders} />
              )}
              {strokes.length > 1 && (
                <FilterPill label={t("live_results_all_strokes")} value={strokeFilter} setValue={setStrokeFilter} options={strokes} labelFor={(s) => t(STROKE_META[s].labelKey)} />
              )}
              {ageCategories.length > 1 && (
                <FilterPill label={t("live_results_all_ages")} value={ageFilter} setValue={setAgeFilter} options={ageCategories} />
              )}
            </div>

            {/* Race card strip */}
            <div className="mb-8 flex gap-3 overflow-x-auto pb-2">
              {filteredRaces.map((ev) => {
                const meta = STROKE_META[ev.stroke]
                const active = ev.event_name === selectedRaceName
                return (
                  <button
                    key={ev.event_name}
                    onClick={() => setSelectedRaceName(ev.event_name)}
                    className={`relative shrink-0 overflow-hidden rounded-lg text-left transition-transform ${active ? "ring-2 ring-gold-400" : "hover:-translate-y-0.5"}`}
                    style={{ width: 176, height: 112, background: `linear-gradient(160deg, ${meta.color}33, #0A1D26)` }}
                  >
                    <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full" style={{ background: meta.color }} />
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink-900/90 to-transparent px-2.5 pb-2 pt-6 text-xs font-semibold leading-tight text-paper-0">
                      {ev.event_name.replace(/^Event\s+\d+\s+/i, "")}
                    </span>
                  </button>
                )
              })}
            </div>

            {selectedRace && (
              <div>
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-xl font-bold text-paper-0">{selectedRace.event_name}</h2>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-mist-500" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t("live_results_search")}
                      aria-label="Search athlete or team"
                      className="h-9 w-56 rounded-lg border border-white/15 bg-ink-800/60 pl-8 pr-3 text-xs text-paper-0 placeholder:text-mist-500 focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20"
                    />
                  </div>
                </div>

                {raceRows.length === 0 ? (
                  <p className="py-16 text-center text-sm text-mist-500">{t("live_results_no_match")}</p>
                ) : (
                  <>
                    {podium.length > 0 && (
                      <div className="mb-8 flex items-end justify-center gap-3">
                        {podiumOrdered.map((row) => {
                          const isFirst = row.rank === "1"
                          const medal = row.rank === "1" ? "🥇" : row.rank === "2" ? "🥈" : "🥉"
                          return (
                            <div
                              key={row.id}
                              className={`flex flex-col items-center rounded-xl border border-white/10 bg-ink-800/60 px-4 pb-4 pt-5 text-center ${isFirst ? "w-40 pt-6" : "w-32"}`}
                              style={isFirst ? { boxShadow: "0 0 0 1px rgba(217,179,108,.15), 0 20px 40px -12px rgba(0,0,0,.6)" } : undefined}
                            >
                              <span className="text-2xl">{medal}</span>
                              <div className={`mt-2 flex items-center justify-center rounded-full bg-gold-500/15 font-display font-bold text-gold-400 ${isFirst ? "h-14 w-14 text-xl" : "h-11 w-11 text-base"}`}>
                                {row.athlete.charAt(0).toUpperCase()}
                              </div>
                              <p className="mt-2 truncate text-sm font-semibold text-paper-0">{row.athlete}</p>
                              <p className="truncate text-xs text-mist-400">{row.team}</p>
                              <p className="mt-1 font-mono text-sm font-bold text-gold-400">{row.time}</p>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {tableRows.length > 0 && (
                      <div className="overflow-x-auto rounded-xl border border-white/10 bg-ink-800/30">
                        <table className="w-full text-left text-sm">
                          <thead className="border-b border-white/10 bg-ink-800/80 text-xs font-semibold uppercase tracking-wider text-mist-400">
                            <tr>
                              <th className="px-4 py-3">{t("live_results_col_rank")}</th>
                              <th className="px-4 py-3">{t("live_results_col_athlete")}</th>
                              <th className="hidden px-4 py-3 sm:table-cell">{t("live_results_col_team")}</th>
                              <th className="px-4 py-3">{t("live_results_col_time")}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {tableRows.map((row) => (
                              <tr key={row.id}>
                                <td className="px-4 py-3 text-mist-300">{row.rank}</td>
                                <td className="px-4 py-3 text-paper-0">
                                  {row.athlete}
                                  <span className="block text-xs text-mist-500 sm:hidden">{row.team}</span>
                                </td>
                                <td className="hidden px-4 py-3 text-mist-300 sm:table-cell">{row.team}</td>
                                <td className="px-4 py-3 font-mono text-mist-300">{row.time}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </main>

      <footer className="border-t border-white/10 py-8 text-center text-[11px] font-medium text-mist-400">
        <Waves className="mb-0.5 mr-1 inline h-3 w-3" aria-hidden="true" />
        {BRAND.copyright}
      </footer>
    </div>
  )
}

function FilterPill({ label, value, setValue, options, labelFor }) {
  return (
    <select
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className="h-8 rounded-full border border-white/15 bg-ink-800/60 px-3 text-xs font-medium text-paper-0 focus:border-gold-400/60 focus:outline-none"
    >
      <option value="">{label}</option>
      {options.map((o) => <option key={o} value={o}>{labelFor ? labelFor(o) : o}</option>)}
    </select>
  )
}
